/**
 * Typed API client for Surveys — Epic 226.
 *
 * Admin-authored questionnaires any user can respond to; admins read responses.
 * Question/answer schemas are opaque JSONB on the backend; the shape below is
 * the frontend contract. Mirrors internal/surveys.
 */

import { apiClient } from '@/lib/apiClient';

export type SurveyAudience = 'all_users' | 'managers' | 'reps';

export interface SurveyQuestion {
  id: string;
  label: string;
}

export interface Survey {
  id: string;
  title: string;
  description: string | null;
  audience: string;
  enabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
}

export interface SurveyResponse {
  id: string;
  respondentUserId: string | null;
  answers: Record<string, unknown>;
  submittedAt: string;
}

export async function listSurveys(): Promise<Survey[]> {
  const { data } = await apiClient.get<Survey[]>('/surveys');
  return data;
}

export interface CreateSurveyRequest {
  title: string;
  description?: string;
  questions?: SurveyQuestion[];
  audience?: string;
}

export async function createSurvey(req: CreateSurveyRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/surveys', req);
  return data;
}

export async function respondToSurvey(
  surveyId: string,
  answers: Record<string, unknown>,
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/surveys/${surveyId}/respond`, { answers });
  return data;
}

export async function listResponses(surveyId: string): Promise<SurveyResponse[]> {
  const { data } = await apiClient.get<SurveyResponse[]>(`/surveys/${surveyId}/responses`);
  return data;
}

// ── Public access (Epic 226 "utilizable"): mint a share token, then anyone
//    with the link can view + respond (optionally anonymously). ──

export interface EnablePublicResult {
  token: string;
  url: string;
}

/** Admin: enable public access for a survey, returning the share token + URL. */
export async function enablePublicSurvey(
  surveyId: string,
  allowAnonymous: boolean,
): Promise<EnablePublicResult> {
  const { data } = await apiClient.post<EnablePublicResult>(
    `/surveys-public-admin/${surveyId}/enable-public`,
    { allowAnonymous },
  );
  return data;
}

export interface PublicSurvey {
  id: string;
  title: string;
  description: string | null;
  questions: SurveyQuestion[];
  allowAnonymous: boolean;
  endsAt: string | null;
}

/** Public (unauthenticated): fetch a survey by its share token. */
export async function viewPublicSurvey(token: string): Promise<PublicSurvey> {
  const { data } = await apiClient.get<PublicSurvey>(`/public/surveys/${token}`);
  return data;
}

/** Public (unauthenticated): submit a response. Email required unless anonymous. */
export async function respondToPublicSurvey(
  token: string,
  answers: Record<string, unknown>,
  respondentEmail?: string,
): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>(`/public/surveys/${token}/respond`, {
    answers,
    respondentEmail: respondentEmail ?? null,
  });
  return data;
}
